import { useLazyQuery, useMutation } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, DatePicker, Form, Select, Skeleton, Switch, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import { getDayJsDate } from '../../utils/datetime';
import ApplicantNamePicker from '../ApplicantNamePicker';
import CustomInput from '../CustomComponents/CustomInput';
import { typeOptions } from './PartyConstants';
import { ADD_PARTICIPANT_MUTATION, GET_PARTICIPANT, UPDATE_PARTICIPANT } from './PartyQueries';

interface ParticipantFormProps {
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  proceedingId: string;
  participantId: string;
  selectedPartyBaId?: string;
}
const ParticipantForm: React.FC<ParticipantFormProps> = ({
  onSubmit,
  onCancel,
  proceedingId,
  participantId,
  selectedPartyBaId,
}) => {
  const [form] = Form.useForm();
  const today = new Date();
  const isAuthenticated = useIsAuthenticated();
  const [shouldShowOtherDescription, setShouldShowOtherDescription] = useState(false);
  const [selectedBaId, setSelectedBaId] = useState<string | null>(null);
  const isEditForm = participantId !== null ? true : false;
  const [socFiler, setSocFiler] = useState(false);

  const [addParticipant, { data: addResult, error: addError }] =
    useMutation(ADD_PARTICIPANT_MUTATION);
  const [updateParticipant, { loading: updating, data: updateResult, error: updateError }] =
    useMutation(UPDATE_PARTICIPANT);

  const toggleOtherDescription = (value: string) => {
    if (value === 'Other') {
      setShouldShowOtherDescription(true);
    } else {
      setShouldShowOtherDescription(false);
    }
  };
  
  const [
    getParticipant,
    { data: participantDetails, loading: loadingDetails, error: loadingError },
  ] = useLazyQuery(GET_PARTICIPANT);

  useEffect(() => {
    form.resetFields();
    if (isEditForm) {
      getParticipant({
        variables: {
          input: {
            proceedingNumber: proceedingId,
            participantId: participantId,
          },
        },
      });
    }
  }, [proceedingId, participantId]);

  useEffect(() => {
    if (participantDetails?.participant) {
      form.setFieldsValue(participantDetails?.participant);
      form.setFieldValue(
        'receiptRequestDate',
        getDayJsDate(participantDetails?.participant.receiptRequestDate)
      );
      form.setFieldValue(
        'decisionIssueDate',
        getDayJsDate(participantDetails?.participant.decisionIssueDate)
      );     
      toggleOtherDescription(participantDetails?.participant.type);
      if (participantDetails?.applicantBaId !== null) {
        setSelectedBaId(participantDetails?.participant.applicantBaId);
      }

      form.setFieldValue('socFiler', participantDetails.participant.socFiler || false); 
      setSocFiler(participantDetails.participant.socFiler || false); 
    }
  }, [participantDetails]);
  const onFinish = async (values: any) => {
    try {
      if (isEditForm) {
        await updateParticipant({
          variables: {
            input: {
              id: participantId,
              proceedingId: proceedingId,
              applicantBaId: selectedBaId || null,
              partyName: values.partyName,
              type: values.type,
              otherParticipantTypeDescription: values.otherParticipantTypeDescription,
              comments: values.comments,
              receiptRequestDate: values.receiptRequestDate,
              decisionIssueDate: values.decisionIssueDate,
              socFiler: socFiler,
            },
          },
        });
      } else {
        await addParticipant({
          variables: {
            input: {
              proceedingId: proceedingId,
              applicantBaId: selectedBaId || null,
              partyName: values.partyName,
              type: values.type,
              otherParticipantTypeDescription: values.otherParticipantTypeDescription,
              comments: values.comments,
              receiptRequestDate: values.receiptRequestDate,
              decisionIssueDate: values.decisionIssueDate,
              socFiler: socFiler,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (
      isEditForm &&
      updateResult !== undefined &&
      updateResult.updateParticipant.result === 'Success'
    ) {
      notification.success({
        message: 'Success',
        description: 'Party has been updated successfully.',
      });
      onSubmit(true);
    }
    if (
      isEditForm &&
      updateResult !== undefined &&
      updateResult.updateParticipant.result === 'Error'
    ) {
      notification.error({
        message: 'Error',
        description: 'An error occured in updating the party.',
      });
      onSubmit(false);
    }
    if (!isEditForm && addResult !== undefined && addResult.addParticipant.result === 'Success') {
      notification.success({
        message: 'Success',
        description: 'Party has been added to the proceeding.',
      });
      form.resetFields();
      onSubmit(true);
    }
    if (!isEditForm && addResult !== undefined && addResult.addParticipant.result === 'Error') {
      notification.error({
        message: 'Error',
        description: 'Error in adding Party to the proceeding.',
      });
      onSubmit(false);
    }
  }, [updateResult, updateError, addResult, addError]);

  const setAsSocFiler = (checked: boolean) => {
    setSocFiler(checked ? true : false);
  };


  return (
    <>
      {(loadingDetails && <Skeleton />) || (
        <div className="thin-border">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="receiptRequestDate" label="Receipt Request Date">
            <DatePicker
              disabled={!isAuthenticated}
              style={{ width: '100%' }}
              disabledDate={(current) => current.isAfter(today)}
            ></DatePicker>
          </Form.Item>
          <Form.Item name="decisionIssueDate" label="Decision Issue Date">
            <DatePicker
              disabled={!isAuthenticated}
              style={{ width: '100%' }}
              disabledDate={(current) => current.isAfter(today)}
            ></DatePicker>
          </Form.Item>
          <Form.Item
            name="partyName"
            label="Name"
            rules={[
              {
                required: true,
                message: 'Please enter the Name or select the company',
              },
            ]}
          >
            <ApplicantNamePicker
              onChange={(value) => form.setFieldsValue({ applicantBaId: value })}
              disabled={ !isAuthenticated}
              onSelect={(baId, isLookup, value) => {
                setSelectedBaId(baId);
                form.setFieldsValue({ partyName: value });
              }}
            />
          </Form.Item>
          <Form.Item
            name="type"
            label="Party Type"
            rules={[{ required: true, message: 'Please select an option' }]}
          >
            <Select
              disabled={!isAuthenticated}
              options={typeOptions}
              onChange={toggleOtherDescription}
            />
          </Form.Item>
          {shouldShowOtherDescription && (
            <Form.Item
              name="otherParticipantTypeDescription"
              label="Other Description"
              rules={
                isAuthenticated && [
                  {
                    required: true,
                    message: 'Please select other description',
                  },
                ]
              }
            >
              <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
            </Form.Item>
          )}
          <Form.Item name="socFiler" label="SOC Filer" valuePropName="checked">
            <Switch  disabled={!isAuthenticated} checked={socFiler === true}   onChange={setAsSocFiler} />
          </Form.Item>
          <Form.Item
            name="comments"
            label="Other Comments"
            rules={[{ required: false, message: 'Please enter the comments.' }]}
          >
            <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
          </Form.Item>
          {isAuthenticated && (
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={onCancel}>
                Cancel
              </Button>
            </Form.Item>
          )}{' '}
          {!isAuthenticated && (
            <Button style={{ marginLeft: 8 }} onClick={onCancel}>
              Back
            </Button>
          )}
        </Form>
        </div>
      )}
    </>
  );
};
export default ParticipantForm;
